<script lang="ts">
  /**
   * P5: per-entity Funnel Journey widget. Shows every funnel the entity is in, with
   * each stage marked reached / current / skipped / future. Skipped = an earlier
   * stage never reached while a later one was (the funnel_transitions history enables
   * this). Reusable on any entity (CONTACT, custom record, …).
   */
  import { onMount } from 'svelte';
  import Icon from './Icon.svelte';
  import { fmtRelative } from '../lib/format';
  import { fetchEntityJourneys, type EntityJourney, type JourneyStage } from '../lib/funnels-api';

  let { entityType, entityId }: { entityType: string; entityId: string } = $props();

  let journeys = $state<EntityJourney[]>([]);
  let loading = $state(true);

  async function load() {
    if (!entityType || !entityId) { loading = false; return; }
    loading = true;
    try {
      journeys = await fetchEntityJourneys(entityType, entityId);
    } catch {
      journeys = [];
    } finally {
      loading = false;
    }
  }
  $effect(() => { void entityType; void entityId; void load(); });

  function dot(state: JourneyStage['state']): { bg: string; ring: string; text: string; icon: 'statusDone' | 'dot' } {
    switch (state) {
      case 'current': return { bg: 'var(--color-pu)', ring: 'var(--color-pu)', text: 'var(--color-text)', icon: 'dot' };
      case 'reached': return { bg: '#16a34a', ring: '#16a34a', text: 'var(--color-text)', icon: 'statusDone' };
      case 'skipped': return { bg: 'transparent', ring: '#d1d5db', text: 'var(--color-text-muted-2)', icon: 'dot' };
      default: return { bg: 'transparent', ring: '#e5e7eb', text: 'var(--color-text-muted-2)', icon: 'dot' };
    }
  }
</script>

<div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4">
  <div class="inline-flex items-center gap-1.5 text-[13px] font-bold mb-3">
    <Icon name="funnels" size={15} /> Воронки
    {#if journeys.length > 0}<span class="text-[var(--color-text-muted)] ml-1 text-xs">{journeys.length}</span>{/if}
  </div>

  {#if loading}
    <div class="text-xs text-[var(--color-text-muted)]">Завантаження…</div>
  {:else if journeys.length === 0}
    <div class="text-[11px] italic text-[var(--color-text-muted-2)]">Не в жодній воронці.</div>
  {:else}
    <div class="flex flex-col gap-4">
      {#each journeys as j (j.funnelSlug)}
        <div>
          <div class="flex items-center justify-between mb-2">
            <div class="text-[12px] font-semibold">{j.funnelName}</div>
            <a href="#/funnels/{j.funnelSlug}/pipeline" class="text-[10px] text-[var(--color-pu-3)] hover:underline">pipeline →</a>
          </div>
          <div class="flex flex-col gap-0.5">
            {#each j.stages as s, i (s.id)}
              {@const d = dot(s.state)}
              <div class="flex items-center gap-2.5">
                <div class="relative flex flex-col items-center">
                  <div class="grid h-4 w-4 place-items-center rounded-full border-2" style="background: {d.bg}; border-color: {d.ring};">
                    {#if s.state === 'reached'}<Icon name="statusDone" size={9} />{/if}
                    {#if s.state === 'current'}<span class="h-1.5 w-1.5 rounded-full bg-white"></span>{/if}
                  </div>
                  {#if i < j.stages.length - 1}<div class="h-3 w-[1.5px]" style="background: {d.ring};"></div>{/if}
                </div>
                <div class="flex-1 min-w-0 -mt-3 pb-1.5">
                  <div class="flex items-center gap-2">
                    <span class="text-[11.5px] truncate {s.state === 'future' || s.state === 'skipped' ? 'text-[var(--color-text-muted-2)]' : 'font-semibold'}" style="color: {d.text};">{s.name}</span>
                    {#if s.state === 'skipped'}<span class="text-[9px] rounded bg-[var(--color-bg-2)] px-1 py-0.5 text-[var(--color-text-muted-2)]">пропущено</span>{/if}
                    {#if s.state === 'current'}<span class="text-[9px] rounded bg-[var(--color-pu-1)] px-1 py-0.5 text-[var(--color-pu)]">зараз</span>{/if}
                  </div>
                  {#if s.reachedAt}<div class="text-[9.5px] text-[var(--color-text-muted-2)]">{fmtRelative(s.reachedAt)}</div>{/if}
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>
